import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ListView,
  ListFetchParams,
  ListFetchResult,
} from "../../../../Core/Components/ListView/ListView";
import {
  RFProfileGroupItem,
  renderRFProfileGroupItem,
} from "../DataColumns/DataColumns";
import {
  listRFProfileGroup,
  deleteRFProfileGroup,
} from "../../Services/RFProfileGroupService";
import { getTokensFromStorage } from "../../../../../Utils/Auth/AuthToken";
import { objectToBase64 } from "../../../../../Utils/Common/commonUtils";
import { buildNotification, GenerateNotification } from "smart-react";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import { MoreVertical } from "lucide-react";
import {
  EVENTS_DATA_TYPES,
  NOTIFICATION_TYPES,
} from "../../../../../constants/eventDataTypes";
import {
  DELETE_MESSAGE,
  ERROR_MESSAGE,
} from "../../../../../constants/notificationMessages";

/* Adapter to map ListView params to the existing service signature
   + client-side search helpers (page 1 + total <= pageSize) */

// Debounce state for search-triggered requests
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let lastSearchTerm = "";

function hasValue(v: any) {
  return v !== null && v !== undefined && String(v).trim() !== "";
}

function formatDateShort(d: string | Date) {
  try {
    const date = typeof d === "string" ? new Date(d) : d;
    if (Number.isNaN(date.getTime())) return String(d);
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  } catch {
    return String(d);
  }
}

async function fetchRFProfileGroups(
  params: ListFetchParams,
): Promise<ListFetchResult<RFProfileGroupItem>> {
  const { page, pageSize, search, sort, filters } = params;
  const { accessToken } = (getTokensFromStorage() as any) || {};

  const term = (search || "").trim();

  // The legacy service expects "limit" (page size) and "offset" as page number
  // It also accepts "filter" and "sort". We keep filters optional for now.
  // Build server-side filters when a search term is present (OR across multiple columns)
  let serverFilters: any = (filters as any) ?? [];
  if (term) {
    const base = [
      { ColumnName: "SRFProfileGroupName", Op: "contains", ColumnValue: term },
      { ColumnName: "TenantName", Op: "contains", ColumnValue: term },
      {
        ColumnName: "SRFProfileGroupDetails",
        Op: "contains",
        ColumnValue: term,
      },
      { ColumnName: "created_by", Op: "contains", ColumnValue: term },
      { ColumnName: "modified_by", Op: "contains", ColumnValue: term },
    ];
    const hasExisting =
      Array.isArray(serverFilters) && serverFilters.length > 0;
    const searchFilters = base.map((f, idx) =>
      idx === 0 && !hasExisting ? f : { LogicalOperator: "OR", ...f },
    );

    serverFilters = Array.isArray(serverFilters)
      ? [...serverFilters, ...searchFilters]
      : searchFilters;
  }

  const doRequest = async (): Promise<ListFetchResult<RFProfileGroupItem>> => {
    const resp = await listRFProfileGroup({
      accessToken,
      limit: pageSize,
      offset: page, // service interprets offset as pageNumber (1-based)
      filter: serverFilters,
      sort: sort ? [{ field: sort.field, dir: sort.dir }] : undefined,
      moduleName: "RFProfileGroup",
      isServiceWorker: false,
      listController: undefined,
    });

    // Service returns { rfProfileGroups, TotalCount } or error-shaped response
    const items = (resp as any)?.rfProfileGroups ?? [];
    const total = (resp as any)?.TotalCount ?? 0;
    return { items, total };
  };

  // Debounce only when the search term changes (typing). For pagination/sort (term unchanged), request immediately.
  if (term !== lastSearchTerm) {
    lastSearchTerm = term;
    if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
    return new Promise<ListFetchResult<RFProfileGroupItem>>((resolve) => {
      searchDebounceTimer = setTimeout(async () => {
        const result = await doRequest();
        resolve(result);
      }, 350); // adjust debounce window if needed
    });
  }

  return await doRequest();
}

const RowActions: React.FC<{ onDeleteClick: () => void }> = ({
  onDeleteClick,
}) => {
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!open) return;
    const handleDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (containerRef.current && !containerRef.current.contains(t)) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleDown);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleDown);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8 rounded-lg bg-muted/40 ring-1 ring-border/60 hover:bg-muted/60"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-label="More actions"
      >
        <MoreVertical className="h-4 w-4 text-foreground" />
      </Button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-40 rounded-md border border-border bg-popover text-popover-foreground shadow-md z-50"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer"
            onClick={() => {
              onDeleteClick();
              setOpen(false);
            }}
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

const RFProfileGroupList: React.FC = () => {
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const deleteConfirmRef = React.useRef<(() => void) | null>(null);
  const [refreshKey, setRefreshKey] = React.useState(0);

  React.useEffect(() => {
    if (!confirmOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setConfirmOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [confirmOpen]);

  const onAdd = React.useCallback(() => {
    navigate("/smartrf/profile-group/new");
  }, [navigate]);

  const onItemClick = React.useCallback(
    (item: RFProfileGroupItem) => {
      const refId = objectToBase64({
        SRFProfileGroupId: item?.SRFProfileGroupId,
        SRFProfileGroupName: item?.SRFProfileGroupName,
        TenantId: item?.TenantId,
        TenantName: item?.TenantName,
      });
      navigate(`/smartrf/profile-group/${item?.TenantId}?refId=${refId}`, {
        state: { edit: true, dataSet: item },
      });
    },
    [navigate],
  );

  const onDelete = React.useCallback(async (selected: RFProfileGroupItem[]) => {
    try {
      for (const it of selected) {
        await deleteRFProfileGroup({
          data: it,
          moduleName: "RFProfileGroup",
          isServiceWorker: false,
          listController: undefined,
        });
      }
      GenerateNotification(
        buildNotification(DELETE_MESSAGE),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
      );
    } catch (e) {
      GenerateNotification(
        buildNotification(ERROR_MESSAGE),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
      );
    }
  }, []);

  const handleDeleteOne = React.useCallback(
    (item: RFProfileGroupItem) => {
      deleteConfirmRef.current = async () => {
        await onDelete([item]);
        setRefreshKey((k) => k + 1);
      };
      setConfirmOpen(true);
    },
    [onDelete],
  );

  const renderItem = (item: RFProfileGroupItem) => {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] items-start sm:items-stretch gap-3">
        <div className="flex-1 min-w-0">{renderRFProfileGroupItem(item)}</div>
        <div className="flex flex-row sm:flex-col justify-between items-center sm:items-end text-left sm:text-right gap-2 w-full sm:w-auto">
          <div className="order-2 sm:order-1 ml-auto sm:ml-0 shrink-0">
            <RowActions onDeleteClick={() => handleDeleteOne(item)} />
          </div>
          <div className="order-1 sm:order-2 flex flex-col items-start sm:items-end text-left sm:text-right gap-1 max-w-full sm:max-w-[220px]">
            {(() => {
              const showUpdated = hasValue((item as any)?.modified_date);
              const dateToShow = showUpdated
                ? (item as any)?.modified_date
                : (item as any)?.created_date;
              const byToShow = showUpdated
                ? (item as any)?.modified_by
                : (item as any)?.created_by;
              return (
                <>
                  <div className="text-sm text-muted-foreground whitespace-nowrap">
                    {formatDateShort(dateToShow as any)}
                  </div>
                  {hasValue(byToShow) && (
                    <div
                      className="text-xs text-muted-foreground/80 truncate"
                      title={String(byToShow)}
                    >
                      {String(byToShow)}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="py-3">
      <ListView<RFProfileGroupItem>
        key={refreshKey}
        idKey="SRFProfileGroupId"
        fetchList={fetchRFProfileGroups}
        itemRenderer={renderItem}
        onItemClick={onItemClick}
        selectionMode="none"
        showCheckboxes={false}
        hideSearch={true}
        selectOnRowClick={false}
        onDelete={onDelete}
        extraToolbarRight={({ search, setSearch }) => (
          <div className="flex items-center gap-2 w-full flex-nowrap">
            <div className="flex-1 min-w-0">
              <Input
                placeholder="Search..."
                value={search}
                onChange={(e) =>
                  setSearch((e.target as HTMLInputElement).value)
                }
                className="h-9 w-full"
              />
            </div>
            <Button
              type="button"
              size="sm"
              onClick={onAdd}
              className="shrink-0 whitespace-nowrap"
            >
              Add New
            </Button>
          </div>
        )}
        initialSort={{ field: "SRFProfileGroupId", dir: "desc" }}
        pageSizeOptions={[10, 20, 50]}
        defaultPageSize={10}
      />
      {confirmOpen && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/40"
            onClick={() => setConfirmOpen(false)}
          />
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
            aria-describedby="confirm-desc"
          >
            <div
              className="w-full max-w-sm rounded-md border border-border bg-popover text-popover-foreground shadow-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-4 py-3 border-b border-border">
                <h2 id="confirm-title" className="text-base font-semibold">
                  Delete selected groups?
                </h2>
              </div>
              <div className="px-4 py-3">
                <p id="confirm-desc" className="text-sm text-muted-foreground">
                  This action cannot be undone. The selected item(s) will be
                  permanently removed.
                </p>
              </div>
              <div className="px-4 py-3 flex justify-end gap-2 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setConfirmOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => {
                    deleteConfirmRef.current?.();
                    setConfirmOpen(false);
                  }}
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RFProfileGroupList;
